import { calculateSurface } from '../shared/calculations/layoutEngine';
import { calculateMaterials } from '../shared/calculations/materials';
import { ProjectState, ProposalClientInfo, SurfaceId } from '../shared/types';
import { clientHeaders } from './clientIdentity';

const loadImage = (source: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error(`Не удалось загрузить ${source.slice(0, 40)}`));
  image.src = source;
});

/**
 * Рисует раскладку поверхности на canvas в пикселях и возвращает dataURL.
 * Отдельный рендерер нужен потому, что SVG, вставленный в <img>, не подгружает
 * внешние текстуры плитки — картинка получилась бы пустой.
 */
export const renderLayoutDataUrl = async (
  project: ProjectState,
  surfaceId: SurfaceId = project.selectedSurface,
): Promise<string | null> => {
  const calculation = calculateSurface(project, surfaceId);
  if (!calculation.tiles.length) return null;

  const maxWidth = 1800;
  const maxHeight = 1400;
  const scale = Math.max(
    0.05,
    Math.min(maxWidth / calculation.width, maxHeight / calculation.height, 3),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(64, Math.round(calculation.width * scale));
  canvas.height = Math.max(64, Math.round(calculation.height * scale));
  const context = canvas.getContext('2d');
  if (!context) return null;

  context.fillStyle = project.grout.color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.scale(scale, scale);

  const textures = new Map<string, HTMLImageElement>();
  const categories = new Set(calculation.tiles.map((placement) => placement.category));
  await Promise.all([...categories].map(async (category) => {
    const tile = project.tiles[category];
    if (!tile?.imagePath) return;
    try {
      textures.set(category, await loadImage(tile.imagePath));
    } catch {
      // Текстура недоступна — плитка будет залита базовым цветом
    }
  }));

  const fallbackColors: Record<string, string> = {
    floor: '#9b9286',
    darkWall: '#35414a',
    lightWall: '#d8d3cb',
    decor: '#7f947f',
    border: '#b58c58',
  };

  calculation.tiles.forEach((placement) => {
    const material = project.tiles[placement.category];
    const texture = textures.get(placement.category);
    context.save();
    if (placement.diagonal) {
      const centerX = placement.x + placement.width / 2;
      const centerY = placement.y + placement.height / 2;
      context.beginPath();
      context.rect(0, 0, calculation.width, calculation.height);
      context.clip();
      context.translate(centerX, centerY);
      context.rotate((placement.rotation * Math.PI) / 180);
      if (material) {
        const sx = Boolean(material.mirror) !== Boolean(placement.mirror) ? -1 : 1;
        const sy = placement.mirrorY ? -1 : 1;
        if (sx !== 1 || sy !== 1) context.scale(sx, sy);
      }
      if (texture) {
        context.drawImage(
          texture,
          -placement.width / 2,
          -placement.height / 2,
          placement.width,
          placement.height,
        );
      } else {
        context.fillStyle = fallbackColors[placement.category] ?? '#cfc9c0';
        context.fillRect(-placement.width / 2, -placement.height / 2, placement.width, placement.height);
      }
    } else if (material) {
      const rotated = material.rotation === 90 || material.rotation === 270;
      const physicalWidth = rotated ? material.tileHeight : material.tileWidth;
      const physicalHeight = rotated ? material.tileWidth : material.tileHeight;
      const centerX = placement.x - placement.sourceX + physicalWidth / 2;
      const centerY = placement.y - placement.sourceY + physicalHeight / 2;
      context.beginPath();
      context.rect(placement.x, placement.y, placement.width, placement.height);
      context.clip();
      context.translate(centerX, centerY);
      context.rotate((placement.rotation * Math.PI) / 180);
      const sx = Boolean(material.mirror) !== Boolean(placement.mirror) ? -1 : 1;
      if (sx !== 1) context.scale(sx, 1);
      if (placement.mirrorY) context.scale(1, -1);
      if (texture) {
        context.drawImage(
          texture,
          -material.tileWidth / 2,
          -material.tileHeight / 2,
          material.tileWidth,
          material.tileHeight,
        );
      } else {
        context.fillStyle = fallbackColors[placement.category] ?? '#cfc9c0';
        context.fillRect(-material.tileWidth / 2, -material.tileHeight / 2, material.tileWidth, material.tileHeight);
      }
    }
    context.restore();
    context.lineWidth = 1 / scale;
    context.strokeStyle = 'rgba(0,0,0,.08)';
    context.strokeRect(placement.x, placement.y, placement.width, placement.height);
  });

  // Проёмы (двери, окна, ниши) — как в 2D-визуализации
  project.objects
    .filter((object) => object.surfaceId === surfaceId)
    .forEach((object) => {
      const top = calculation.height - object.y - object.height;
      const width = Math.min(object.width, calculation.width);
      const height = Math.min(object.height, calculation.height);
      const x = Math.min(Math.max(0, object.x), Math.max(0, calculation.width - width));
      if (object.type === 'window') {
        context.fillStyle = '#c8e2f0';
        context.fillRect(x, top, width, height);
        context.strokeStyle = '#f2efe8';
        context.lineWidth = Math.max(6, width * 0.05);
        context.strokeRect(x, top, width, height);
        return;
      }
      context.fillStyle = object.type === 'door' ? '#e8e0d2' : '#5d6362';
      context.fillRect(x, top, width, height);
      context.strokeStyle = 'rgba(20,27,29,.5)';
      context.lineWidth = Math.max(4, width * 0.02);
      context.strokeRect(x, top, width, height);
    });

  return canvas.toDataURL('image/jpeg', 0.92);
};

/** Снимок 3D-сцены: canvas появляется только в режиме «3D» */
export const captureRoom3dDataUrl = (): string | null => {
  const canvas = document.querySelector<HTMLCanvasElement>('.room-canvas-3d');
  if (!canvas) return null;
  try {
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch {
    return null;
  }
};

export interface ProposalResult {
  blob: Blob;
  proposalId: string | null;
  proposalUrl: string | null;
  total: number;
}

export const buildProposal = async (
  project: ProjectState,
  client: ProposalClientInfo,
  images: { room3d?: string | null; layout?: string | null } = {},
): Promise<ProposalResult> => {
  const response = await fetch('/api/export/pdf', {
    method: 'POST',
    headers: clientHeaders(true),
    body: JSON.stringify({
      project,
      client,
      save: true,
      images: {
        room3d: images.room3d ?? undefined,
        layout: images.layout ?? undefined,
      },
    }),
  });
  if (!response.ok) throw new Error('Сервер не сформировал КП');

  return {
    blob: await response.blob(),
    proposalId: response.headers.get('x-proposal-id'),
    proposalUrl: response.headers.get('x-proposal-url'),
    total: Number(response.headers.get('x-proposal-total') ?? 0),
  };
};

export const localProposalTotals = (project: ProjectState) => calculateMaterials(project);

export interface SendResult {
  ok: boolean;
  mode: 'whatsapp' | 'link';
  url: string;
  error?: string;
}

export const sendProposal = async (
  proposalId: string,
  phone: string,
  message?: string,
): Promise<SendResult> => {
  const response = await fetch(`/api/proposals/${proposalId}/send`, {
    method: 'POST',
    headers: clientHeaders(true),
    body: JSON.stringify({ phone, message }),
  });
  const payload = await response.json().catch(() => ({})) as SendResult & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? 'Не удалось отправить КП');
  }
  return payload;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
